import axios, {
  AxiosHeaders,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import discovery from "@wkt/discovery";
import { getAccessToken } from "./auth-context";
import { buildDeviceHeaders } from "./device";
import { deferPeriodClosedFallback } from "./httpErrors";

let currentServiceVersion: string | null = null;
export const SERVICE_VERSION_UPDATED_EVENT = "wkt:service-version-updated";

function canUseWindow(): boolean {
  return typeof window !== "undefined";
}

function normalizeUrl(url?: string | null): string | null {
  if (typeof url !== "string" || url.trim().length === 0) {
    return null;
  }

  if (!canUseWindow()) {
    return url;
  }

  try {
    return new URL(url, window.location.origin).toString();
  } catch (_) {
    return url;
  }
}

function isWorktimeServiceRequest(url?: string | null): boolean {
  const normalizedUrl = normalizeUrl(url);
  const normalizedServiceUrl = normalizeUrl(discovery.worktime_service_url);

  if (!normalizedUrl || !normalizedServiceUrl) {
    return false;
  }

  return normalizedUrl.startsWith(normalizedServiceUrl);
}

function isLegacyInternetExplorer(): boolean {
  if (!canUseWindow()) {
    return false;
  }

  return /MSIE|Trident/.test(window.navigator.userAgent);
}

function applyWorktimeHeaders(
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig {
  if (!isWorktimeServiceRequest(config.url)) {
    return config;
  }

  const headers = AxiosHeaders.from(config.headers ?? {});
  const accessToken = getAccessToken();
  if (accessToken) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }

  const deviceHeaders = buildDeviceHeaders();
  Object.entries(deviceHeaders).forEach(([key, value]) => {
    headers.set(key, value);
  });

  if (isLegacyInternetExplorer()) {
    headers.set("Pragma", "no-cache");
    headers.set("Cache-Control", "no-cache, no-store");
  }

  config.headers = headers;
  return config;
}

function handleServiceVersion(response?: AxiosResponse | null): void {
  if (!response || !isWorktimeServiceRequest(response.config?.url)) {
    return;
  }

  const nextVersionHeader =
    response.headers?.["x-service-version"] ??
    response.headers?.["X-SERVICE-VERSION"];

  if (typeof nextVersionHeader !== "string" || nextVersionHeader.trim().length === 0) {
    return;
  }

  const nextVersion = nextVersionHeader.trim();
  if (currentServiceVersion == null) {
    currentServiceVersion = nextVersion;
    return;
  }

  if (currentServiceVersion === nextVersion) {
    return;
  }

  currentServiceVersion = nextVersion;
  if (canUseWindow()) {
    window.dispatchEvent(
      new CustomEvent(SERVICE_VERSION_UPDATED_EVENT, {
        detail: { version: nextVersion },
      }),
    );
  }
}

export const httpClient = axios.create({});

httpClient.interceptors.request.use((config) => applyWorktimeHeaders(config));

httpClient.interceptors.response.use(
  (response) => {
    handleServiceVersion(response);
    return response;
  },
  (error) => {
    handleServiceVersion(error?.response);
    const normalizedError = deferPeriodClosedFallback(error, (conflict) => {
      if (canUseWindow()) {
        window.dispatchEvent(new CustomEvent("wkt:period-closed", {
          detail: {
            closureIds: conflict.closureIds,
            protectedFromInclusive: conflict.protectedFromInclusive,
            protectedToInclusive: conflict.protectedToInclusive,
          },
        }));
      }
    });
    return Promise.reject(normalizedError);
  },
);

export function getCurrentServiceVersion(): string | null {
  return currentServiceVersion;
}

export function primeServiceVersion(version: string | null): void {
  currentServiceVersion =
    typeof version === "string" && version.trim().length > 0
      ? version.trim()
      : null;
}
